import React, { useState, useEffect } from "react";
import { ChatKit, useChatKit } from "@openai/chatkit-react";
import { Box, Fab, IconButton, Paper, Typography } from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import { assistantPageCodeEnum } from "features/AssistantPages/constantTypes";
import { IChatKitSession, openChatKitSession } from "api/openChatKitSession";

interface IAssistantChatProps {
  code: assistantPageCodeEnum;
  propertyId?: string;
}

export const AssistantChat = ({ code, propertyId }: IAssistantChatProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [session, setSession] = useState<IChatKitSession | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSession(null);

    openChatKitSession({ code, propertyId }).then((data) => {
      if (!cancelled) setSession(data);
    });

    return () => {
      cancelled = true;
    };
  }, [code, propertyId]);

  const { control } = useChatKit({
    api: {
      async getClientSecret(existing) {
        if (existing) return existing;

        return session?.clientSecret ?? "";
      },
    },
    initialThread: session?.lastThreadId ?? undefined,
    composer: {
      attachments: {
        enabled: true,
      },
    },
  });

  const toggleChat = () => {
    setIsVisible((prev) => !prev);
  };

  return (
    <>
      <Fab
        color="primary"
        aria-label="chat"
        onClick={toggleChat}
        sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 1000 }}
      >
        {isVisible ? <CloseIcon /> : <ChatIcon />}
      </Fab>

      <Paper
        elevation={6}
        sx={{
          position: "fixed",
          bottom: 90,
          right: 24,
          width: "500px",
          height: "min(800px, calc(100vh - 120px))",
          display: isVisible ? "flex" : "none",
          flexDirection: "column",
          overflow: "hidden",
          zIndex: 1000,
          borderRadius: "12px",
        }}
      >
        <Box
          sx={{
            p: 2,
            bgcolor: "primary.main",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="subtitle1" color="inherit">
            AI Assistant
          </Typography>

          <IconButton size="small" color="inherit" onClick={toggleChat}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ flexGrow: 1, position: "relative" }}>
          {session && (
            <ChatKit key={`${code}_${propertyId}`} control={control} />
          )}
        </Box>
      </Paper>
    </>
  );
};
